import {PayloadAction, createSlice} from '@reduxjs/toolkit';

import {EStorageKeys} from 'src/constants';
import {listenerMiddleware} from 'src/models';

import {EFormStep, TFilters} from './definitions';

export type TFormState = {
  activeStep: keyof typeof EFormStep;
  steps: Record<string, {isVisited: boolean; isInvalid?: boolean}>;
};

const initialFromState: TFormState = {
  activeStep: EFormStep.DETAILS,
  steps: {
    [EFormStep.DETAILS]: {
      isVisited: true,
    },
    [EFormStep.CALCULATION_AND_LICENSES]: {
      isVisited: false,
    },
    [EFormStep.TARIFFS]: {
      isVisited: false,
    },
  },
};

const initialState = () => {
  const rememberedFilters = sessionStorage.getItem(EStorageKeys.LEAD_COMPANY_CLIENTS_FILTERS);

  return {
    filters: rememberedFilters ? JSON.parse(rememberedFilters) : {},
    formState: initialFromState,
  };
};

const slice = createSlice({
  name: 'leadClients',
  initialState,
  reducers: {
    setFilters(state, action: PayloadAction<TFilters>) {
      state.filters = action.payload;
    },
    setActiveFormStep(state, action: PayloadAction<keyof typeof EFormStep>) {
      state.formState.activeStep = action.payload;
      state.formState.steps[action.payload].isVisited = true;
    },
    setLastFormStep(state) {
      state.formState.activeStep = Object.keys(state.formState.steps).pop() as EFormStep;
      state.formState.steps = Object.keys(state.formState.steps).reduce((result, step) => {
        return {...result, [step]: {isVisited: true}};
      }, {});
    },
    setActiveFormStepIsInvalid(state, action: PayloadAction<boolean>) {
      state.formState.steps[state.formState.activeStep].isInvalid = action.payload;
    },
    resetFormState(state) {
      state.formState = {...initialFromState};
    },
    setBillingDisabledSteps(state, action: PayloadAction<boolean>) {
      const isBillingDisabled = action.payload;

      if (isBillingDisabled) {
        delete state.formState.steps[EFormStep.TARIFFS];
      } else {
        state.formState.steps[EFormStep.TARIFFS] = {isVisited: false};
      }
    },
  },
});

export const {
  setFilters: setFiltersAction,
  setActiveFormStep,
  setActiveFormStepIsInvalid,
  setLastFormStep,
  setBillingDisabledSteps,
  resetFormState,
} = slice.actions;
export const selectFilters = (state: TRootState) => state.leadClients.filters as TFilters;
export const selectFormSteps = (state: TRootState) => state.leadClients.formState.steps;
export const selectFormActiveStep = (state: TRootState) => state.leadClients.formState.activeStep;
export const selectIsFormInvalidStep = (state: TRootState) =>
  !!state.leadClients.formState.steps[state.leadClients.formState.activeStep]?.isInvalid;
export const selectIsFormFirstStep = (state: TRootState) =>
  state.leadClients.formState.activeStep === Object.keys(state.leadClients.formState.steps)[0];
export const selectIsFormLastStep = (state: TRootState) =>
  state.leadClients.formState.activeStep === Object.keys(state.leadClients.formState.steps).pop();

listenerMiddleware.startListening({
  actionCreator: setFiltersAction,
  effect: (action) => {
    sessionStorage.setItem(
      EStorageKeys.LEAD_COMPANY_CLIENTS_FILTERS,
      JSON.stringify(action.payload),
    );
  },
});

export default slice.reducer;
